import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Save, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const LANGS = [["en", "English"], ["de", "Deutsch"], ["ar", "العربية"], ["tr", "Türkçe"]];
const emptyI18n = () => ({ en: { title: "", description: "" }, de: { title: "", description: "" }, ar: { title: "", description: "" }, tr: { title: "", description: "" } });

export const ConsultationTypesManager = ({ types, onReload }) => {
  const [rows, setRows] = useState([]);
  const [nt, setNt] = useState({ id: "", name: "" });

  useEffect(() => {
    setRows(types.map((t) => ({ ...t, i18n: { ...emptyI18n(), ...(t.i18n || {}) } })));
  }, [types]);

  const set = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));
  const setI18n = (id, lang, field, v) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, i18n: { ...r.i18n, [lang]: { ...r.i18n[lang], [field]: v } } } : r)));

  const save = async (r) => {
    try {
      const { data } = await api.patch(`/admin/consultation-types/${r.id}`, {
        name: r.i18n.en.title || r.name, duration: Number(r.duration) || 0, fee: Number(r.fee) || 0,
        deposit: Number(r.deposit) || 0, format: r.format, active: r.active,
        payment_required: r.payment_required, i18n: r.i18n,
      });
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...data, i18n: { ...emptyI18n(), ...(data.i18n || {}) } } : x)));
      toast.success("Consultation saved");
      onReload && onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save consultation");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this consultation type? Existing bookings are not affected.")) return;
    await api.delete(`/admin/consultation-types/${id}`);
    setRows((rs) => rs.filter((r) => r.id !== id));
    toast.success("Consultation deleted");
    onReload && onReload();
  };

  const create = async () => {
    if (!nt.name.trim()) { toast.error("Enter a title"); return; }
    const id = (nt.id || nt.name).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const i18n = emptyI18n();
    i18n.en.title = nt.name.trim();
    const { data } = await api.post(`/admin/consultation-types`, {
      id, name: nt.name.trim(), duration: 30, fee: 0, deposit: 0, format: "both", active: true, payment_required: false, i18n,
    }).catch((e) => { toast.error(e.response?.data?.detail || "Could not create"); throw e; });
    setRows((rs) => [...rs, { ...data, i18n: { ...emptyI18n(), ...(data.i18n || {}) } }]);
    setNt({ id: "", name: "" });
    toast.success("Consultation added");
    onReload && onReload();
  };

  return (
    <div className="space-y-4">
      {/* Add new */}
      <div className="flex flex-wrap items-end gap-3 rounded-3xl border border-dashed border-champagne-gold/50 bg-warm-ivory p-5" data-testid="type-add">
        <div className="flex-1 min-w-[220px]">
          <p className="mb-1.5 text-sm text-deep-ink/70">New consultation title (English)</p>
          <Input data-testid="new-type-name" value={nt.name} onChange={(e) => setNt({ ...nt, name: e.target.value })} placeholder="e.g. Menopause Deep-Dive" className="bg-sand" />
        </div>
        <div className="w-48">
          <p className="mb-1.5 text-sm text-deep-ink/70">ID (optional)</p>
          <Input value={nt.id} onChange={(e) => setNt({ ...nt, id: e.target.value })} placeholder="auto from title" className="bg-sand" />
        </div>
        <Button onClick={create} data-testid="create-type" className="gap-2 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink"><Plus className="h-4 w-4" />Add consultation</Button>
      </div>

      {rows.map((r) => (
        <div key={r.id} className="rounded-3xl bg-warm-ivory p-6" data-testid={`type-card-${r.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline" className="font-mono">{r.id}</Badge>
            <div className="ms-auto flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-deep-ink/70">Active <Switch checked={!!r.active} onCheckedChange={(v) => set(r.id, "active", v)} data-testid={`type-active-${r.id}`} /></label>
              <button onClick={() => remove(r.id)} data-testid={`delete-type-${r.id}`} className="text-terracotta hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>

          <Tabs defaultValue="en" className="mt-4">
            <TabsList className="bg-sand">
              {LANGS.map(([code, label]) => <TabsTrigger key={code} value={code} data-testid={`type-lang-${r.id}-${code}`}>{label}</TabsTrigger>)}
            </TabsList>
            {LANGS.map(([code]) => (
              <TabsContent key={code} value={code} className="mt-3 space-y-3">
                <Input value={r.i18n[code].title} onChange={(e) => setI18n(r.id, code, "title", e.target.value)} placeholder="Title" className="bg-sand" data-testid={`type-title-${r.id}-${code}`} />
                <Textarea value={r.i18n[code].description} onChange={(e) => setI18n(r.id, code, "description", e.target.value)} placeholder="Description" rows={2} className="bg-sand" data-testid={`type-desc-${r.id}-${code}`} />
              </TabsContent>
            ))}
          </Tabs>

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <label className="text-sm text-deep-ink/70">Duration (min)<Input type="number" value={r.duration} onChange={(e) => set(r.id, "duration", e.target.value)} className="mt-1 w-24 bg-sand" /></label>
            <label className="text-sm text-deep-ink/70">Fee<Input type="number" value={r.fee} onChange={(e) => set(r.id, "fee", e.target.value)} className="mt-1 w-24 bg-sand" /></label>
            <label className="text-sm text-deep-ink/70">Format
              <Select value={r.format} onValueChange={(v) => set(r.id, "format", v)}>
                <SelectTrigger className="mt-1 w-32 bg-sand"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="online">Online</SelectItem><SelectItem value="in_person">In person</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
              </Select>
            </label>
            <Button onClick={() => save(r)} data-testid={`save-type-${r.id}`} className="ms-auto gap-2 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink"><Save className="h-4 w-4" />Save</Button>
          </div>
        </div>
      ))}
    </div>
  );
};
